import { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

import { Header } from './components/Header.jsx';
import { Footer } from './components/Footer.jsx';
import { FloatingApply } from './components/FloatingApply.jsx';

import { HomePage } from './pages/HomePage.jsx';
import { AboutPage } from './pages/AboutPage.jsx';
import { AdmissionPage } from './pages/AdmissionPage.jsx';
import { ContactPage } from './pages/ContactPage.jsx';
import { NewsListPage } from './pages/NewsListPage.jsx';
import { NewsDetailPage } from './pages/NewsDetailPage.jsx';
import { EventsPage } from './pages/EventsPage.jsx';
import { AchievementsPage } from './pages/AchievementsPage.jsx';
import { AlumniPage } from './pages/AlumniPage.jsx';
import { AlumniRegistrationPage } from './pages/AlumniRegistrationPage.jsx';
import { PhotosPage } from './pages/PhotosPage.jsx';
import { AcademicCalendarPage } from './pages/AcademicCalendarPage.jsx';
import { FacilitiesPage } from './pages/FacilitiesPage.jsx';
import { CareerPage } from './pages/CareerPage.jsx';
import { DynamicPage } from './pages/DynamicPage.jsx';
import { NotFoundPage } from './pages/NotFoundPage.jsx';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [pathname]);
  return null;
}

function RevealOnScroll() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          e.target.classList.add('visible');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.12 });

    const observeAll = () => {
      document.querySelectorAll('.reveal:not(.is-visible)').forEach((el) => io.observe(el));
    };
    observeAll();

    // Content often loads from the API *after* this route mounts (e.g. the
    // facility blocks, news, events). Watch the DOM and observe any `.reveal`
    // elements added later — otherwise they stay stuck at opacity:0.
    const mo = typeof MutationObserver !== 'undefined'
      ? new MutationObserver(observeAll)
      : null;
    mo?.observe(document.body, { childList: true, subtree: true });

    return () => { io.disconnect(); mo?.disconnect(); };
  }, [pathname]);
  return null;
}

export function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <RevealOnScroll />
        <Header />
        <main>
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/admission" element={<AdmissionPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/news" element={<NewsListPage />} />
              <Route path="/news/:slug" element={<NewsDetailPage />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/achievements" element={<AchievementsPage />} />
              <Route path="/photos" element={<PhotosPage />} />
              <Route path="/alumni" element={<AlumniPage />} />
              <Route path="/alumni/register" element={<AlumniRegistrationPage />} />
              <Route path="/calendar" element={<AcademicCalendarPage />} />
              <Route path="/page/facilities" element={<FacilitiesPage />} />
              <Route path="/career" element={<CareerPage />} />

              {/* All other CMS-backed pages: about, principals-message, scholarship,
                  library, lab, ielts, igcse, ial, pg-kg, i-v, vi-ix, etc. */}
              <Route path="/page/:slug" element={<DynamicPage />} />

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
        <FloatingApply />
      </BrowserRouter>
    </HelmetProvider>
  );
}
